import {
  Badge,
  Box,
  Button,
  IconButton,
  Td,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FaPlay } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import PreviewModal from "../components/common/Modal/PreviewModal";
import CreateTable from "../components/common/Table/Table";
import Loading from "../components/common/ui/Loading";
import { useNavigator } from "../hooks/useNavigator";
import { getAdLeaderboard } from "../utils/api";
import { videoAPI } from "../utils/bunnyApi";

const getMovieFromEntry = (entry) => {
  if (!entry) return null;
  if (Array.isArray(entry.movie) && entry.movie.length > 0) {
    return entry.movie[0];
  }
  if (entry.movie && typeof entry.movie === "object") {
    return entry.movie;
  }
  return null;
};

const getPrizeForRank = (priceList, rank) => {
  if (!priceList?.length || !rank) return null;
  const item = priceList.find((p) => {
    const key = Object.keys(p)[0];
    return String(key) === String(rank);
  });
  if (!item) return null;
  const key = Object.keys(item)[0];
  return item[key];
};

const getVideoUrl = (entry, movie) => {
  if (movie?.url) return movie.url;
  if (movie?.mp4Url) return movie.mp4Url;
  if (entry?.url) return entry.url;
  const videoId = movie?.videoId || entry?.videoId;
  if (videoId) return videoAPI.getPlaylistUrl(videoId);
  return null;
};

const normalizeLeaderboardEntries = (entries, priceList) =>
  (entries || []).map((entry, idx) => {
    const movie = getMovieFromEntry(entry);
    const rank = idx + 1;
    return {
      ...entry,
      rank,
      prize: getPrizeForRank(priceList, rank),
      name: entry.name || "N/A",
      movieName: `${entry.user?.name} (${entry.user.phoneNumber || entry.user.email})` || "N/A",
      movieUrl: getVideoUrl(entry, movie),
      likes: entry.likes ?? 0,
      entryDate: entry.createdAt || movie?.createdAt,
    };
  });

const AdLeaderboard = () => {
  const { id } = useParams();
  const toast = useToast();
  const nav = useNavigate();
  const [leaderboardInfo, setLeaderboardInfo] = useState(null);
  const [movieList, setMovieList] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    isOpen: isPreviewOpen,
    onOpen: onPreviewOpen,
    onClose: onPreviewClose,
  } = useDisclosure();
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("Preview");

  const handlePlay = (url, title) => {
    if (!url) {
      toast({
        title: "No video",
        description: "Video URL is not available for this entry",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setPreviewUrl(url);
    setPreviewTitle(title);
    onPreviewOpen();
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    const [success, data] = await getAdLeaderboard(id);
    if (success) {
      const info = data?.leaderboard || null;
      setLeaderboardInfo(info);
      setMovieList(
        normalizeLeaderboardEntries(
          Array.isArray(data?.movie) ? data.movie : [],
          info?.priceList
        )
      );
    } else {
      toast({
        title: "Error",
        description: "Failed to fetch leaderboard",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  const [navigate, user] = useNavigator(() => {
    if (user) {
      fetchLeaderboard();
    } else {
      navigate("/login");
    }
  });

  if (loading) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "1rem",
        gap: "1rem",
      }}
    >
      <PreviewModal
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        url={previewUrl}
        title={previewTitle}
        urlType="video"
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text fontSize="xl" fontWeight="bold">
          Event Leaderboard
        </Text>
        <Button colorScheme="gray" onClick={() => nav("/ads")}>
          Back to Event
        </Button>
      </Box>

      {leaderboardInfo && (
        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "0.5rem",
            padding: "1rem",
            backgroundColor: "#f7fafc",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1.5rem",
              alignItems: "center",
            }}
          >
            <Box>
              <Text fontSize="sm" color="gray.500">
                Price Money
              </Text>
              <Text fontSize="lg" fontWeight="bold">
                {leaderboardInfo.priceMoney || "N/A"}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Status
              </Text>
              <Badge
                colorScheme={leaderboardInfo.isClosed ? "red" : "green"}
                fontSize="sm"
              >
                {leaderboardInfo.isClosed ? "Closed" : "Active"}
              </Badge>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Created
              </Text>
              <Text fontSize="sm">
                {leaderboardInfo.createdAt
                  ? new Date(leaderboardInfo.createdAt).toLocaleString()
                  : "N/A"}
              </Text>
            </Box>
          </Box>

          {leaderboardInfo.priceList &&
            leaderboardInfo.priceList.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  Price List
                </Text>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {leaderboardInfo.priceList.map((item, idx) => {
                    const key = Object.keys(item)[0];
                    return (
                      <Badge
                        key={idx}
                        colorScheme="purple"
                        variant="subtle"
                        px={2}
                        py={1}
                      >
                        Rank {key}: {item[key]}
                      </Badge>
                    );
                  })}
                </Box>
              </Box>
            )}
        </Box>
      )}

      {movieList && movieList.length > 0 ? (
        <CreateTable
          data={movieList}
          searchby={["name", "movieName"]}
          pagging={true}
          head={[
            "#",
            "Name",
            "Prize",
            "User",
            "Votes",
            "Date",
            "Play",
          ]}
          rows={(row) => {
            return (
              <>
                <Td>{row.rank}</Td>
                <Td>{row.name}</Td>
                <Td>
                  {row.prize != null ? (
                    <Badge colorScheme="green" variant="subtle">
                      {row.prize}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td>{row.movieName}</Td>
                <Td>{row.likes}</Td>
                <Td>
                  {row.entryDate
                    ? new Date(row.entryDate).toLocaleDateString()
                    : "N/A"}
                </Td>
                <Td>
                  <IconButton
                    aria-label="Play video"
                    icon={<FaPlay />}
                    size="sm"
                    colorScheme="teal"
                    variant="solid"
                    isRound
                    onClick={() =>
                      handlePlay(row.movieUrl, row.movieName || "Video")
                    }
                    isDisabled={!row.movieUrl}
                  />
                </Td>
              </>
            );
          }}
        />
      ) : (
        <Text>No entries found</Text>
      )}
    </Box>
  );
};

export default AdLeaderboard;
